import type { Subscription } from "rxjs";
import { zip as observableZip } from "rxjs";

import { cloneDeep, isEmpty } from "lodash";
import { finalize } from "rxjs/operators";

import type { IComponentController, IScope } from "angular";

import type { ISiteFunctionalitiesRepository, ISitesRepository } from "App/Modules/Repositories";
import type { IDefaultNamingService, IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";

import type {
    IConfigurationGroup,
    IConfigurationGroupDetection,
    IFunctionality,
    IFunctionalityCare,
    IResponse,
    IResponseWithLoading,
    ISiteEquipmentSlim,
    ISiteEquipmentWithCoverages,
} from "App/Modules/Models";
import { ConfigurationGroupType, EquipmentClass } from "App/Modules/Models";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditCareFunctionalityComponentController implements IComponentController {
    public static $inject = [
        "$scope",
        "mmSiteFunctionalitiesRepository",
        "mmSitesRepository",
        "irDefaultNamingService",
    ];

    public originalFunctionality: IFunctionality;

    public functionality: IFunctionality;

    public configurationGroups: IConfigurationGroup[];

    public computerSiteEquipment: ISiteEquipmentSlim[];

    public typeName: string;

    public saveResponse: IResponseWithLoading<IFunctionality>;

    public deleteResponse: IResponseWithLoading<void>;

    public errorResponse: IResponse<IFunctionalityCare | IConfigurationGroupDetection[]>;

    public resolve: IFormContainerResolve<
        [IFunctionality, string, boolean],
        ISiteEquipmentWithCoverages,
        IFunctionality
    >;

    public isDataLoading = true;

    public subscription: Subscription;

    public isEditable: boolean;

    constructor(
        private readonly $scope: IScope,
        private readonly siteFunctionalitiesRepository: ISiteFunctionalitiesRepository,
        private readonly sitesRepository: ISitesRepository,
        private readonly defaultNamingService: IDefaultNamingService,
    ) {}

    public $onInit() {
        const [fn, siteId, isEditable]: [IFunctionality, string, boolean] = this.resolve.formData;
        this.isEditable = isEditable;

        this.typeName = fn.typeName;

        const mdFunctionality$ = this.siteFunctionalitiesRepository.getFunctionality<IFunctionalityCare>(siteId, fn.id);

        const configurationGroups$ = this.sitesRepository.getConfigurationGroups(siteId, ConfigurationGroupType.Care);

        const computerSiteEquipment$ = this.sitesRepository.getSiteEquipments(siteId, EquipmentClass.Computer);

        this.subscription = observableZip(configurationGroups$, mdFunctionality$, computerSiteEquipment$)
            .pipe(
                finalize(() => {
                    this.isDataLoading = false;
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                ([configurationGroups, functionality, computerSiteEquipment]) => {
                    this.configurationGroups = configurationGroups.data as IConfigurationGroupDetection[];
                    this.configurationGroups.forEach((cg) => this.defaultNamingService.nameConfigurationGroup(cg));

                    this.computerSiteEquipment = computerSiteEquipment.data;
                    this.functionality = functionality.data;
                    this.originalFunctionality = cloneDeep(this.functionality);
                },
                (err: IResponse<IFunctionalityCare | IConfigurationGroupDetection[]>) => {
                    this.errorResponse = err;
                },
            );
    }

    public $onDestroy() {
        this.subscription.unsubscribe();
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editFunctionality() {
        const patchObject = PatchDelta.difference(this.originalFunctionality, this.functionality);

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.siteFunctionalitiesRepository.patchFunctionality(
                    this.resolve.formData[1],
                    this.originalFunctionality.id,
                    this.functionality.type,
                    patchObject,
                ),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (saveResponse: IResponseWithLoading<IFunctionalityCare>) => {
                        this.saveResponse = saveResponse;
                        if (!saveResponse.isLoading && saveResponse.data) {
                            this.resolve.options.onOk(saveResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.saveResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public deleteFunctionality() {
        extendWithLoadingAndFail(
            this.siteFunctionalitiesRepository.deleteFunctionality(this.resolve.formData[1], this.functionality.id),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;
                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (errorResponse) => {
                    this.deleteResponse = errorResponse;
                },
            );
    }
}
